import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { sessionsAPI, photosAPI, feedbackAPI, certificatesAPI, SessionDetails, Photo, Feedback, FeedbackSummary } from '../api/client';
import './ProofSummary.css';

/**
 * ProofSummary - Summary page after all photos are reviewed
 *
 * Shows all photos with their feedback status, and provides
 * a summary of approvals vs change requests.
 */
const ProofSummary = () => {
  const { sessionId } = useParams<{ sessionId: string }>();

  // State
  const [session, setSession] = useState<SessionDetails | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [feedback, setFeedback] = useState<Record<string, Feedback>>({});
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Load session data
  useEffect(() => {
    const loadSession = async () => {
      if (!sessionId) {
        setError('Invalid session link');
        setLoading(false);
        return;
      }

      try {
        const { session: sessionData } = await sessionsAPI.get(sessionId);
        setSession(sessionData);
        setPhotos(sessionData.photos || []);

        // Convert feedback array/object to record
        if (sessionData.feedback) {
          if (Array.isArray(sessionData.feedback)) {
            const feedbackMap: Record<string, Feedback> = {};
            sessionData.feedback.forEach((fb: Feedback) => {
              feedbackMap[fb.photo_id] = fb;
            });
            setFeedback(feedbackMap);
          } else {
            setFeedback(sessionData.feedback);
          }
        }

        // Get feedback summary
        const { summary: summaryData } = await feedbackAPI.getSummary(sessionId);
        setSummary(summaryData);
      } catch (err: any) {
        console.error('Failed to load session:', err);
        if (err.response?.status === 410) {
          setError('This proof session has expired. Please contact Seraphina Grey for assistance.');
        } else {
          setError('Unable to load proof session. Please check your link and try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, [sessionId]);

  // Get status display info
  const getSessionStatus = () => {
    if (!session || !summary) return { label: 'Pending', className: 'pending' };

    if (summary.pending_count > 0) {
      return { label: 'In Progress', className: 'in-progress' };
    }
    // Check if ALL photos were approved (no changes requested AND no rejections)
    if (summary.needs_changes_count === 0 && (summary.rejected_count || 0) === 0) {
      return { label: 'All Approved', className: 'approved' };
    }
    // Check if ALL photos were rejected or need changes (none approved)
    if (summary.approved_count === 0) {
      return { label: 'Changes Requested', className: 'rejected' };
    }
    // Mixed: some approved, some rejected/needs changes
    return { label: 'Mixed Feedback', className: 'mixed' };
  };

  const status = getSessionStatus();

  // Check if session is completed (has all photos reviewed)
  const isSessionCompleted = summary && summary.pending_count === 0 && summary.total_photos > 0;

  // Handle certificate download
  const handleDownloadCertificate = async () => {
    if (!sessionId || isGenerating) return;

    setIsGenerating(true);
    setDownloadError(null);

    try {
      // Generate certificate (backend will return cached if exists)
      await certificatesAPI.generate(sessionId);

      // Open download URL in new tab
      window.open(certificatesAPI.getDownloadUrl(sessionId), '_blank');
    } catch (err: any) {
      console.error('Failed to generate certificate:', err);
      if (err.response?.status === 400) {
        setDownloadError('All photos must be reviewed before downloading the certificate.');
      } else if (err.response?.status === 410) {
        setDownloadError('This session has expired.');
      } else {
        setDownloadError('Failed to generate certificate. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="proof-summary">
        <div className="summary-loading">
          <div className="summary-spinner"></div>
          <p>Loading summary...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="proof-summary">
        <div className="summary-error">
          <h2>Unable to Load Summary</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="proof-summary">
      {/* Header */}
      <header className="summary-header">
        <div className="summary-header-content">
          <div className="summary-brand">
            <span className="brand-name">SERAPHINA GREY</span>
            <span className="brand-divider">|</span>
            <span className="brand-subtitle">PROOF SUMMARY</span>
          </div>
          <div className="summary-customer">
            {session?.customer_name}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="summary-main">
        {/* Summary card */}
        <div className="summary-card">
          <h1 className="summary-title">Review Complete</h1>
          <p className="summary-subtitle">
            Thank you for reviewing your proofs!
          </p>

          {/* Status badge */}
          <div className={`summary-status ${status.className}`}>
            {status.label}
          </div>

          {/* Stats */}
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-value">{photos.length}</span>
              <span className="stat-label">Total Photos</span>
            </div>
            <div className="stat approved">
              <span className="stat-value">{summary?.approved_count || 0}</span>
              <span className="stat-label">Approved</span>
            </div>
            <div className="stat changes">
              <span className="stat-value">{summary?.needs_changes_count || 0}</span>
              <span className="stat-label">Changes Requested</span>
            </div>
            {(summary?.rejected_count || 0) > 0 && (
              <div className="stat rejected">
                <span className="stat-value">{summary?.rejected_count}</span>
                <span className="stat-label">Rejected</span>
              </div>
            )}
            {(summary?.pending_count || 0) > 0 && (
              <div className="stat pending">
                <span className="stat-value">{summary?.pending_count}</span>
                <span className="stat-label">Pending</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="summary-actions">
            <Link
              to={`/proof/${sessionId}`}
              className="btn-back"
            >
              Back to Photos
            </Link>
            {isSessionCompleted && (
              <button
                className="btn-download"
                onClick={handleDownloadCertificate}
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Download Certificate'}
              </button>
            )}
            {downloadError && (
              <p className="download-error">{downloadError}</p>
            )}
          </div>
        </div>

        {/* Photo grid */}
        <div className="summary-photos">
          <h2 className="photos-title">Your Reviewed Photos</h2>
          <div className="photos-grid">
            {photos.map((photo, index) => {
              const photoFeedback = feedback[photo.id];
              return (
                <div
                  key={photo.id}
                  className={`photo-card ${photoFeedback?.approval_status || 'pending'}`}
                >
                  <div className="photo-image-wrapper">
                    <img
                      src={photosAPI.getThumbnailUrl(photo)}
                      alt={photo.original_filename}
                      loading="lazy"
                    />
                    {/* Version badge for updated photos */}
                    {photo.version_number && photo.version_number > 1 && (
                      <span className="photo-version-badge">v{photo.version_number}</span>
                    )}
                    <div className={`photo-overlay ${photoFeedback?.approval_status || 'pending'}`}>
                      {photoFeedback ? (
                        photoFeedback.approval_status === 'approve' ? (
                          <span className="overlay-icon">&#10003;</span>
                        ) : (
                          <span className="overlay-icon">&#9998;</span>
                        )
                      ) : (
                        <Link
                          to={`/proof/${sessionId}?photo=${index}`}
                          className="overlay-review-link"
                        >
                          Review
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="photo-details">
                    <span className="photo-number">Photo {index + 1}</span>
                    <span className={`photo-status ${photoFeedback?.approval_status || 'pending'}`}>
                      {photoFeedback
                        ? photoFeedback.approval_status === 'approve'
                          ? 'Approved'
                          : photoFeedback.approval_status === 'rejected'
                          ? 'Rejected'
                          : 'Changes Requested'
                        : 'Pending'}
                    </span>
                    {photoFeedback?.feedback_text && (
                      <p className="photo-note">{photoFeedback.feedback_text}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Message for changes requested */}
        {summary && summary.needs_changes_count > 0 && (
          <div className="summary-message">
            <h3>What's Next?</h3>
            <p>
              Your designer has been notified of your feedback.
              They will review your change requests and may reach out
              to discuss the revisions.
            </p>
          </div>
        )}

        {/* Message for all approved */}
        {summary && summary.needs_changes_count === 0 && (summary.rejected_count || 0) === 0 && summary.pending_count === 0 && (
          <div className="summary-message success">
            <h3>All Photos Approved!</h3>
            <p>
              Your designer has been notified that you've approved
              all your proofs. You can now download your certificate above.
            </p>
          </div>
        )}

        {/* Message for mixed feedback (some approved, some rejected) */}
        {summary && summary.pending_count === 0 && summary.approved_count > 0 && ((summary.rejected_count || 0) > 0 || summary.needs_changes_count > 0) && (
          <div className="summary-message mixed">
            <h3>Review Complete</h3>
            <p>
              Your designer has been notified of your feedback.
              {(summary.rejected_count || 0) > 0 && ` ${summary.rejected_count} photo${(summary.rejected_count || 0) !== 1 ? 's were' : ' was'} rejected.`}
              {summary.approved_count > 0 && ` ${summary.approved_count} photo${summary.approved_count !== 1 ? 's were' : ' was'} approved.`}
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="summary-footer">
        <p>
          Questions? Contact your designer directly or reply to your
          invitation email.
        </p>
      </footer>
    </div>
  );
};

export default ProofSummary;
